<script setup lang="ts">
import BackstageInput from '@/components/common/BackstageInput.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import IconLogo from '@/components/icons/IconLogo.vue';
import LoggedOutFrame from '@/layouts/LoggedOutFrame.vue';
import { PopUpType } from '@/models/pop-up-type.enum';
import { PasswordResetService } from '@/services/password-reset.service';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const username = ref('');
const notification = ref('');
const isLoading = ref(false);
const passwordResetService = new PasswordResetService();
const { t } = useI18n();

const isSubmitButtonDisabled = computed(() => {
  return username.value === '' || isLoading.value;
});

async function resetPassword(event: Event) {
  event.preventDefault();
  isLoading.value = true;

  const response = await passwordResetService.resetPasswordFromUsername(username.value);

  isLoading.value = false;
  notification.value = t(response).toString();
}
</script>

<template>
  <LoggedOutFrame :background-image-url="$t('resetPassword.background_image_url')" :header="$t('resetPassword.header')">
    <form class="form" v-on:submit="resetPassword">
      <div class="header">
        <IconLogo data-cy="logo"></IconLogo>
      </div>
      <div class="header-text">
        {{ $t('resetPassword.form_header') }}
      </div>
      <BackstageInput
        v-model="username"
        class="reset-password-input"
        :label="$t('resetPassword.username')"
        type="text"
        name="reset-password-username"
        data-cy-input="reset-password-username-field"
        autocomplete="username" />
      <input
        type="submit"
        class="button submit-button"
        data-cy="reset-password-button"
        :value="$t('resetPassword.button_label')"
        :disabled="isSubmitButtonDisabled" />
    </form>
    <div class="link">
      <router-link to="/login" data-cy="back-to-login-link" class="login-link">
        {{ $t('login.go_to_login') }}
      </router-link>
    </div>
    <NotificationMessage v-if="notification" :type="PopUpType.Success" v-on:close="notification = ''">
      {{ $t(notification) }}
    </NotificationMessage>
  </LoggedOutFrame>
</template>

<style lang="scss" scoped>
.form {
  .header {
    margin-bottom: 2rem;

    :deep(svg) {
      display: flex;
      width: 16.5px;
    }
  }

  .header-text {
    text-align: left;
    margin-bottom: 1rem;
  }

  .reset-password-input {
    display: block;
    margin-bottom: 2rem;
  }

  .submit-button {
    width: 100%;
    margin-bottom: 1.5rem;
  }
}
.link {
  .login-link {
    color: var(--primary-text-color);
    text-decoration: underline;
    display: block;
  }
}
</style>
